import React, { useEffect, useState } from "react"
import { toast } from "react-toastify"
import { Button, Modal, Form } from "react-bootstrap"
import PaginationTable from "../../components/paginationTable"
import SearchBar from "../../components/searchBar"
import {
    getAllTeamMembers,
    getTeamMembersCount,
    createTeamMember,
    updateTeamMembers,
    deleteTeamMember,
} from "../../actions/commonController"
import { isEmpty } from "../../config/funcs"

const TeamMembers = () => {
    const [data, setData] = useState([])
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [showEditModal, setShowEditModal] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [memberToDelete, setMemberToDelete] = useState(null)
    const [formValue, setFormValue] = useState({
        name: "",
        position: "",
        email: "",
        linkedIn: "",
        img: null
    })
    const [totalItems, setTotalItems] = useState(0)
    const [itemsPerPage, setItemsPerPage] = useState(10)
    const [page, setPage] = useState(1)
    const [quantity, setQuantity] = useState(10)
    const [searchTerm, setSearchTerm] = useState("")
    const [modalLoader, setModalLoader] = useState(false)
    const [editModalLoader, setEditModalLoader] = useState(false)
    const [deleteLoader, setDeleteLoader] = useState(false)
    const [loader, setLoader] = useState(false)
    const [initLoad, setInitLoad] = useState(false)
    const [imagePreview, setImagePreview] = useState(null)

    const handleChange = (e) => {
        const { name, value, files } = e.target
        if (name === 'img' && files && files[0]) {
            const file = files[0]
            setFormValue(prev => ({
                ...prev,
                img: file
            }))
            // Create preview
            const reader = new FileReader()
            reader.onload = (e) => setImagePreview(e.target.result)
            reader.readAsDataURL(file)
        } else {
            setFormValue(prev => ({
                ...prev,
                [name]: value,
            }))
        }
    }

    const openCreateModal = () => {
        setFormValue({
            name: "",
            position: "",
            email: "",
            linkedIn: "",
            img: null
        })
        setImagePreview(null)
        setShowCreateModal(true)
    }

    const closeCreateModal = () => {
        setFormValue({
            name: "",
            position: "",
            email: "",
            linkedIn: "",
            img: null
        })
        setImagePreview(null)
        setShowCreateModal(false)
    }

    const handlePageChange = (page) => {
        setPage(page)
    }

    const handleItemsPerPageChange = (newItemsPerPage) => {
        setQuantity(newItemsPerPage)
        setPage(1)
    }

    const handleEdit = async (memberId) => {
        try {
            const member = data.find(m => m.id === memberId)
            if (member) {
                setFormValue({
                    id: member.id,
                    name: member.name,
                    position: member.position,
                    email: member.email,
                    linkedIn: member.linkedIn,
                    img: null
                })
                setImagePreview(member.img || null)
                setShowEditModal(true)
            }
        } catch (error) {
            toast.error("An error occurred while fetching team member details")
        }
    }

    const closeEditModal = () => {
        setFormValue({
            name: "",
            position: "",
            email: "",
            linkedIn: "",
            img: null
        })
        setImagePreview(null)
        setShowEditModal(false)
    }

    const handleCreateMember = async () => {
        try {
            if (!formValue.name || !formValue.position || !formValue.email) {
                toast.error("Please fill in all required fields")
                return
            }

            setModalLoader(true)

            const formData = new FormData()
            formData.append('name', formValue.name)
            formData.append('position', formValue.position)
            formData.append('email', formValue.email)
            formData.append('linkedIn', formValue.linkedIn)
            if (formValue.img) {
                formData.append('img', formValue.img)
            }

            const { status, error } = await createTeamMember(formData)
            if (status === "success") {
                toast.success("Team member created successfully!")
                closeCreateModal()
                setPage(1)
                await loadData()
                await refreshTotalCount()
            } else {
                toast.error(error || "Failed to create team member")
            }
        } catch (err) {
            toast.error("An error occurred while creating the team member")
        } finally {
            setModalLoader(false)
        }
    }

    const handleUpdateMember = async () => {
        try {
            if (!formValue.name || !formValue.position || !formValue.email) {
                toast.error("Please fill in all required fields")
                return
            }

            setEditModalLoader(true)

            const formData = new FormData()
            formData.append('id', formValue.id)
            formData.append('name', formValue.name)
            formData.append('position', formValue.position)
            formData.append('email', formValue.email)
            formData.append('linkedIn', formValue.linkedIn)
            if (formValue.img) {
                formData.append('img', formValue.img)
            }

            const { status, error } = await updateTeamMembers(formData)
            if (status === "success") {
                toast.success("Team member updated successfully")
                closeEditModal()
                loadData()
            } else {
                toast.error(error || "Failed to update team member")
            }
        } catch (error) {
            toast.error("An error occurred while updating the team member")
        } finally {
            setEditModalLoader(false)
        }
    }

    const handleDelete = (memberId) => {
        setMemberToDelete(memberId)
        setShowDeleteModal(true)
    }

    const handleConfirmDelete = async () => {
        if (memberToDelete) {
            setDeleteLoader(true)
            try {
                const { status, error } = await deleteTeamMember(memberToDelete)
                if (status === "success") {
                    toast.success("Team member deleted successfully")
                    setShowDeleteModal(false)
                    setPage(1)
                    await loadData()
                    await refreshTotalCount()
                } else {
                    toast.error(error || "Failed to delete team member")
                }
            } catch (err) {
                toast.error("An error occurred while deleting the team member")
            } finally {
                setDeleteLoader(false)
            }
        }
    }

    const initialization = async () => {
        try {
            setLoader(true)
            let { status, data } = await getTeamMembersCount()

            if (status === "success") {
                setTotalItems(data)
                setPage(1)
                await loadData()
                setInitLoad(true)
            }
        } catch (err) {
            console.error("Error in initialization function", err)
        }
    }

    const refreshTotalCount = async () => {
        try {
            let { status, data } = await getTeamMembersCount();
            if (status === "success") {
                setTotalItems(data);
            }
        } catch (err) {
            console.error("Error refreshing count:", err);
        }
    };

    const loadData = async () => {
        try {
            let APIData = {
                page: page,
                quantity: quantity,
            }

            setLoader(true)
            const { data } = await getAllTeamMembers(APIData)
            setData(data)
        } catch (error) {
            console.error("Error fetching team members:", error)
        } finally {
            setLoader(false)
        }
    }

    useEffect(() => {
        if (initLoad) loadData()
    }, [page, quantity])

    useEffect(() => {
        initialization()
    }, [])

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="page-title-box">
                        <div className="page-title-right">
                            <Button variant="primary" onClick={openCreateModal}>
                                <i className="ri-add-line"></i> Add Team Member
                            </Button>
                        </div>
                        <h4 className="page-title">Team Members</h4>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            {loader ? (
                                <div className="text-center">
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-centered table-hover table-nowrap mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>S. No</th>
                                                <th>Name</th>
                                                <th>Position</th>
                                                <th>Email</th>
                                                <th>LinkedIn</th>
                                                {/* <th>Image</th> */}
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.map((member, index) => (
                                                <tr key={member.id}>
                                                    <td>{(page - 1) * quantity + index + 1}</td>
                                                    <td>{member.name}</td>
                                                    <td>{member.position}</td>
                                                    <td>{member.email}</td>
                                                    <td>
                                                        {member.linkedIn && (
                                                            <a href={member.linkedIn} target="_blank" rel="noopener noreferrer">
                                                                {member.linkedIn}
                                                            </a>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="btn btn-sm btn-outline-primary me-1"
                                                            onClick={() => handleEdit(member.id)}
                                                        >
                                                            <i className="ri-edit-line"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleDelete(member.id)}
                                                        >
                                                            <i className="ri-delete-bin-line"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {data.length === 0 && (
                                        <div className="text-center py-4">
                                            <p>No team members found. Add your first team member!</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Pagination Component */}
                    <PaginationTable
                        totalItems={totalItems}
                        itemsPerPage={itemsPerPage}
                        pageChange={handlePageChange}
                        onItemsPerPageChange={handleItemsPerPageChange}
                    />
                </div>

                {/* Create Team Member Modal */}
                <Modal size="lg" show={showCreateModal} onHide={closeCreateModal} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Add New Team Member</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <div className="row">
                                <div className="col-md-6">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Name *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            value={formValue.name}
                                            onChange={handleChange}
                                            placeholder="Enter full name"
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Position *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="position"
                                            value={formValue.position}
                                            onChange={handleChange}
                                            placeholder="Enter position"
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Email *</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="email"
                                            value={formValue.email}
                                            onChange={handleChange}
                                            placeholder="Enter email"
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group className="mb-3">
                                        <Form.Label>LinkedIn URL</Form.Label>
                                        <Form.Control
                                            type="url"
                                            name="linkedIn"
                                            value={formValue.linkedIn}
                                            onChange={handleChange}
                                            placeholder="Enter LinkedIn URL"
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                            <Form.Group className="mb-3">
                                <Form.Label>Profile Image</Form.Label>
                                <Form.Control
                                    type="file"
                                    name="img"
                                    accept="image/*"
                                    onChange={handleChange}
                                />
                                {imagePreview && (
                                    <div className="mt-2">
                                        <img
                                            src={imagePreview}
                                            alt="Preview"
                                            style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                            className="rounded"
                                        />
                                    </div>
                                )}
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={closeCreateModal}>Close</Button>
                        {modalLoader ? (
                            <button className="btn btn-primary" disabled>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Creating...
                            </button>
                        ) : (
                            <Button variant="primary" onClick={handleCreateMember}>
                                Create Team Member
                            </Button>
                        )}
                    </Modal.Footer>
                </Modal>

                {/* Edit Team Member Modal */}
                <Modal size="lg" show={showEditModal} onHide={closeEditModal} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Edit Team Member</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <div className="row">
                                <div className="col-md-6">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Name *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            value={formValue.name}
                                            onChange={handleChange}
                                            placeholder="Enter full name"
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Position *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="position"
                                            value={formValue.position}
                                            onChange={handleChange}
                                            placeholder="Enter position"
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Email *</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="email"
                                            value={formValue.email}
                                            onChange={handleChange}
                                            placeholder="Enter email"
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group className="mb-3">
                                        <Form.Label>LinkedIn URL</Form.Label>
                                        <Form.Control
                                            type="url"
                                            name="linkedIn"
                                            value={formValue.linkedIn}
                                            onChange={handleChange}
                                            placeholder="Enter LinkedIn URL"
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                            <Form.Group className="mb-3">
                                <Form.Label>Profile Image</Form.Label>
                                <Form.Control
                                    type="file"
                                    name="img"
                                    accept="image/*"
                                    onChange={handleChange}
                                />
                                {imagePreview && (
                                    <div className="mt-2">
                                        <img
                                            src={imagePreview}
                                            alt="Preview"
                                            style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                            className="rounded"
                                        />
                                    </div>
                                )}
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={closeEditModal}>Close</Button>
                        {editModalLoader ? (
                            <button className="btn btn-primary" disabled>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Saving...
                            </button>
                        ) : (
                            <Button variant="primary" onClick={handleUpdateMember}>
                                Save Changes
                            </Button>
                        )}
                    </Modal.Footer>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Delete</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Are you sure you want to delete this team member?</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>No</Button>
                        {deleteLoader ? (
                            <button className="btn btn-danger" disabled>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Deleting...
                            </button>
                        ) : (
                            <Button variant="danger" onClick={handleConfirmDelete}>
                                Yes, Delete
                            </Button>
                        )}
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    )
}

export default TeamMembers